import { Box, Card, CardBody, CardProps, Flex, FlexProps, HStack, Skeleton, Stack, StackProps } from "@chakra-ui/react";
import { Heading16 } from "components/Heading";
import { IssueDetail, IssueId } from "models/issue";
import InfoExpander from "components/InfoExpander";
import { Text14 } from "components/Text";
import { ActionV2 } from "features/Resources/Shared/models";
import NextIssueButton from "./NextPreviousButton";
import { useIssueResolution } from "../Resolutions/useIssueResolution";
import { useIssueDetailQuery, useIsIssueResolvedQuery } from "../hooks";
import { isTimingIssue } from "../helpers";
import { IndividualActionTimingIssueModalInputFields } from "../Resolutions/definitions/TimingResolution";
import resolveIssueType from "../Resolutions/resolveIssueType";
import IssueStatusTag from "./IssueStatusTag";
import IssueActionStatusTag from "./IssueActionStatusTag";

export function IssueCardSkeleton({ ...rest }: StackProps) {
  return (
    <Stack padding={4} spacing={1} w="340px" {...rest}>
      <Skeleton height="40px" />
      <Skeleton height="40px" />
    </Stack>
  );
}

interface IssueCardHeaderProps extends FlexProps {
  issueId: IssueId;
  title: string;
  additionalContext?: React.ReactNode;
}

function IssueCardHeader({ issueId, title, additionalContext, ...rest }: IssueCardHeaderProps) {
  return (
    <Flex direction="column" {...rest}>
      <HStack justify="space-between">
        <Heading16>{title}</Heading16>
        <IssueStatusTag issueId={issueId} />
      </HStack>
      <Box my="2">
        <InfoExpander buttonText="More Info">{additionalContext ?? "Loading..."}</InfoExpander>
      </Box>
    </Flex>
  );
}

export function ActionIssueResolutionContent({
  issueId,
  action,
  skeletonProps = {},
}: {
  issueId: IssueId;
  action: ActionV2;
  skeletonProps?: object;
}) {
  const { data: issue, isLoading } = useIssueDetailQuery(issueId);
  const { title, actionTitle, additionalContext, inputComponent: InputComponent } = useIssueResolution(issue);
  const displayTitle = actionTitle ? actionTitle(action) : title;
  return (
    <>
      <Flex direction="column">
        <HStack justify="space-between">
          <Heading16>{displayTitle}</Heading16>
          <IssueActionStatusTag issueId={issueId} actionPrefixId={action?.prefixId} />
        </HStack>
        <Box my="2">
          <InfoExpander buttonText="More Info">{additionalContext ?? "Loading..."}</InfoExpander>
        </Box>
      </Flex>
      {isLoading || !issue ? (
        <IssueCardSkeleton {...skeletonProps} w="100%" />
      ) : isTimingIssue(issue) ? (
        <IndividualActionTimingIssueModalInputFields issue={issue} action={action} />
      ) : (
        <InputComponent issue={issue} />
      )}
    </>
  );
}

export function IssueResolutionContent({
  issue,
  issues = [],
  includeNext = false,
  skeletonProps = {},
}: {
  issue: IssueDetail;
  issues?: IssueDetail[];
  includeNext?: boolean;
  skeletonProps?: object;
}) {
  // first see if it's resolved
  const { data: isResolved, isLoading: resolvedIsLoading } = useIsIssueResolvedQuery(issue?.id);
  // then pull the context for the issue (new hire journey, user_id, attribute name, etc)
  const { data: extendedIssue, isLoading: issueIsLoading, isFetched: issueIsFetched } = useIssueDetailQuery(issue?.id);
  // dynamically get the resolution information / components
  const { title, additionalContext, inputComponent: InputComponent } = useIssueResolution(extendedIssue);
  return (
    <>
      <IssueCardHeader
        issueId={issue?.id}
        title={issueIsFetched ? title : (resolveIssueType(issue) as string)}
        {...(!isTimingIssue(issue) ? { status: isResolved?.resolved ? "resolved" : "unresolved" } : {})}
        additionalContext={additionalContext}
      />
      {resolvedIsLoading || issueIsLoading ? (
        <IssueCardSkeleton {...skeletonProps} />
      ) : isTimingIssue(issue) && isResolved?.resolved ? (
        <Text14>No Actions to Resolve</Text14>
      ) : (
        <InputComponent issue={issue} />
      )}
      {includeNext && (
        <Flex justify="space-between">
          <NextIssueButton issueId={issue?.id} issues={issues} />
        </Flex>
      )}
    </>
  );
}

export default function IssueCard({
  issue,
  issues = [],
  includeNext = false,
  ...rest
}: { issue: IssueDetail; issues?: IssueDetail[]; includeNext?: boolean } & CardProps) {
  if (!issue || !issues) {
    return <IssueCardSkeleton />;
  }
  return (
    <Card width="fit-content" {...rest}>
      <CardBody>
        <IssueResolutionContent issue={issue} issues={issues} includeNext={includeNext} />
      </CardBody>
    </Card>
  );
}
